import * as HLF from "html-living-framework";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {Callback} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import {Assets, FreePlayAssets, getBgmAsset, getLocationBackgroundAsset, PostcardAssets} from "../data/Assets";
import {IMatch2ScreenData} from "../data/IMatch2ScreenData";
import {LevelNumber} from "../data/LevelModel";
import {Locations} from "../data/Location";
import {OnboardingStep} from "../data/OnboardingStep";
import {PersistentGameModel} from "../data/PersistentGameModel";
import {getLocationNameVO, voOnboarding, voOptionsButtons, voOptionsDirections} from "../data/VoiceOver";
import {ButtonPulse} from "../gameobjects/Button";
import {ButtonMute} from "../gameobjects/ButtonMute";
import {Envelope, EnvelopeTransform} from "../gameobjects/Envelope";
import {Globe} from "../gameobjects/Globe";
import {IdleVO} from "../gameobjects/IdleVO";
import {Keyboard} from "../gameobjects/Keyboard";
import {LocationBackground} from "../gameobjects/LocationBackground";
import {Luna} from "../gameobjects/Luna";
import {OnboardingContainer} from "../gameobjects/OnboardingContainer";
import {VOWatcher} from "../gameobjects/VOWatcher";
import {LocationBackgroundLayout_main} from "../layout/LocationBackgroundLayout";
import {SelectLocationLayout_main} from "../layout/SelectLocationLayout";
import {AudioChannel} from "../managers/AudioChannel";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {AbstractGameState} from "./AbstractGameState";
import {FreePlayScreen} from "./FreePlayScreen";
import {GameScreen} from "./GameScreen";

export class SelectLocationScreen extends AbstractGameState {

    public static readonly NAME = "SelectLocationScreen";

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new LocationBackground());
    public readonly envelope = this.main.addChild(new Envelope());
    public readonly envelopeSelected = new EnvelopeTransform();
    public readonly globe = this.main.addChild(new Globe(Locations));
    public readonly frontClouds = this.main.addChild(new Sprite());
    public readonly luna = this.main.addChild(new Luna());
    public readonly buttonBack = this.main.addChild(new ButtonPulse(undefined, this.onClickBack.bind(this)));
    public readonly buttonNext = this.main.addChild(new ButtonPulse(undefined, this.onClickNext.bind(this)));
    public readonly buttonFreePlay = this.main.addChild(new ButtonPulse(undefined, this.onClickFreePlay.bind(this)));
    public readonly buttonMute = this.addChild(new ButtonMute());
    public readonly onboardingContainer = this.addChild(new OnboardingContainer());

    private _locationIndex = 0;
    private _locationSelected = false;
    private _fromStartScreen = false;
    private readonly _idleVO = new IdleVO("patw_ui03", "patw_ui06", "patw_ui07");
    private readonly _voWatchers: VOWatcher[] = [
        new VOWatcher(voOnboarding[OnboardingStep.SwitchLocation]!, 0, () => {
            this.buttonBack.pulse(1.2, 0, true);
            this.buttonNext.pulse(1.2, 0, true);
        }, () => {
            this.buttonBack.stopPulse(false);
            this.buttonNext.stopPulse(false);
        }),
    ];

    public init(pSize: PIXI.Point, pData?: IMatch2ScreenData) {
        super.init(pSize);

        SelectLocationLayout_main.call(this);
        LocationBackgroundLayout_main.call(this);
        this.main.scale.set(0.5);

        if (pData && pData.location) {
            this._locationIndex = Locations.indexOf(pData.location);
        }
        if (pData) {
            this._fromStartScreen = pData.fromStartScreen || false;
        }

        const location = Locations[this._locationIndex];
        this.background.setLocation(location);
        this.globe.setLocation(location);
        this.envelope.setLocation(location);
        this.envelope.onClick.add(() => {
            AudioChannel.SFX.play(Assets.audio.envelopeClick.assetName);
            this.envelope.enabled = false;
            this.gotoGameState(GameScreen.NAME, this.getScreenData());
        });
        this.envelope.onOver.add(() => {
            VoiceOverManager.instance.playVO("patw_ui13", voOptionsButtons);
        });

        this.buttonBack.hoverVO = "patw_ui19";
        this.buttonNext.hoverVO = "patw_ui12";
        this.buttonFreePlay.hoverVO = "patw_ui20";

        if (!Assets.audio.bgm.menu.getAsset().isPlaying()) {
            AudioChannel.BGM.stop();
            AudioChannel.BGM.play(Assets.audio.bgm.menu.assetName, "loop");
        }
    }

    public animateIn(cb: Callback) {
        this.lockScreen();

        this.envelope.enabled = false;
        this.envelope.moveYFrom(this._size.y + this.envelope.height, 0.6, Ease.backOut, {
            delay: this._fromStartScreen ? 0.6 : 0.2,
            onStart: () => AudioChannel.SFX.play(Assets.audio.newPostcard.assetName),
            onComplete: () => {
                this.envelope.pulse(1.1, 1.5, true);
            },
        });
        this.background.zoomOut(0.6, () => {
            this.unlockScreen();
            cb();
        });

        this.globe.pulseLocation(this._fromStartScreen ? 0.9 : 0.5);
        this.globe.moveYFrom(this.globe.y + this._size.y * 1.5, this._fromStartScreen ? 1 : 0.6, Ease.sineOut);
        this.frontClouds.moveYFrom(this.frontClouds.y + this.frontClouds.height, 0.2, Ease.sineOut);
        this.luna.moveYFrom(this.luna.y + this.luna.characterSpine.height, this._fromStartScreen ? 0.3 : 0.2, Ease.sineOut, {
            delay: this._fromStartScreen ? 0.8 : 0.4,
            onComplete: () => {
                this.luna.idleBlink();
                const voOptions = {
                    ...voOptionsDirections,
                    callback: () => {
                        this.startOnboarding();
                    },
                };
                if (PersistentGameModel.getFreePlayUnlocked() && !PersistentGameModel.getFreePlayVisited()) {
                    VoiceOverManager.instance.playVO("patw_ui08", voOptions);
                } else if (this._fromStartScreen) {
                    VoiceOverManager.instance.playVO("patw_ui02", voOptions);
                } else {
                    VoiceOverManager.instance.playVO("patw_ui04", voOptions);
                }
                this.buttonBack.enabled = true;
                this.buttonNext.enabled = true;
                this.buttonFreePlay.enabled = true;
                this.envelope.enabled = true;
            },
        });

        this.buttonBack.enabled = false;
        this.buttonNext.enabled = false;
        this.buttonFreePlay.enabled = false;

        this.buttonBack.tweenFrom(0.3, Ease.sineOut, {
            delay: 0.6,
            alpha: 0,
            scaleX: this.buttonBack.scale.x * 0.4,
            scaleY: this.buttonBack.scale.y * 0.4,
        });
        this.buttonNext.tweenFrom(0.3, Ease.sineOut, {
            delay: 0.6,
            alpha: 0,
            scaleX: this.buttonNext.scale.x * 0.4,
            scaleY: this.buttonNext.scale.y * 0.4,
        });
        if (PersistentGameModel.getFreePlayUnlocked()) {
            this.buttonFreePlay.moveYFrom(this._size.y + this.buttonFreePlay.height, 0.4, Ease.backOut, {
                delay: 0.6,
                onComplete: () => {
                    if (!PersistentGameModel.getFreePlayVisited()) {
                        this.buttonFreePlay.pulse(1.2, 1, true);
                    }
                },
            });
        } else {
            this.buttonFreePlay.visible = false;
        }

        Keyboard.add(
            this.envelope,
            this.buttonBack,
            this.buttonNext,
            this.buttonMute,
            this.buttonFreePlay,
        );
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions) {
        this._idleVO.destroy();
        for (const watcher of this._voWatchers) {
            watcher.destroy();
        }
        super.destroy(pOptions);
    }

    private startOnboarding() {
        const onboardingStarted = this.onboardingContainer.showOnboardingStep(OnboardingStep.SwitchLocation, this.main, [], () => {
            this.onboardingContainer.showOnboardingStep(OnboardingStep.SelectLocation, this.main, [
                {
                    x: this.envelope.getTransformBackup().x + this.envelope.width * 0.7,
                    y: this.envelope.getTransformBackup().y,
                    d: "left",
                },
            ], () => {
                this.startIdleInterval();
            });
        });
        if (onboardingStarted) {
            this._voWatchers[0]?.start();
        }
    }

    private getScreenData() {
        const location = Locations[this._locationIndex];
        return {
            location,
            level: PersistentGameModel.getNextLocationLevel(location) as LevelNumber,
        };
    }

    private onClickFreePlay() {
        this.gotoGameState(FreePlayScreen.NAME, this.getScreenData());
    }

    private onClickBack() {
        this.changeLocation(-1);
    }

    private onClickNext() {
        this.changeLocation(1);
    }

    private changeLocation(direction: number) {
        this._locationIndex += direction;
        while (this._locationIndex < 0) {
            this._locationIndex += Locations.length;
        }
        while (this._locationIndex >= Locations.length) {
            this._locationIndex -= Locations.length;
        }

        this.buttonBack.enabled = false;
        this.buttonNext.enabled = false;

        const duration = 1;
        const location = Locations[this._locationIndex];

        this.background.changeLocation(location, duration);

        this.stopIdleInterval();
        this.envelope.enabled = false;
        this.envelope.hide(0.2, () => {
            this.envelope.setLocation(location);
            this.envelope.delayedCall(duration - 0.6, () => {
                this.envelope.show(0.4, () => {
                    this.startOnboarding();
                    this.envelope.enabled = true;
                    this.playLocationVO();
                });
            });
        });
        AudioChannel.SFX.play(Assets.audio.spin.assetName);
        this.globe.tweenTo(duration, Ease.sineInOut, {
            rotation: -2 * Math.PI * direction,
            onComplete: () => {
                this.globe.rotation = 0;
                this.buttonBack.enabled = true;
                this.buttonNext.enabled = true;
            },
        });
        this.globe.delayedCall(duration / 2, () => {
            this.globe.setLocation(location);
            this.globe.pulseLocation(duration * 0.4);
        });
    }

    private gotoGameState(screen: string, data: IMatch2ScreenData) {
        this.lockScreen();
        this.stopIdleInterval();
        this.onboardingContainer.hideOnboardingStep();
        this._locationSelected = true;

        const isFreePlay = screen === FreePlayScreen.NAME;

        if (isFreePlay) {
            this.envelope.moveYTo(this._size.y + this.envelope.height, 0.2, Ease.sineIn);
        } else {
            this.envelope.stopPulse();
            this.envelope.tweenTo(0.4, Ease.sineInOut, {
                x: this.envelopeSelected.x,
                y: this.envelopeSelected.y,
                scaleX: this.envelopeSelected.scaleX,
                scaleY: this.envelopeSelected.scaleY,
            });
        }

        this.background.zoomIn(0.6, () => {
            if (isFreePlay) {
                HLF.AssetMap.addAssetGroup(FreePlayScreen.NAME, [
                    ...FreePlayAssets,
                    getLocationBackgroundAsset(data.location),
                ]);
            } else {
                const bgm = getBgmAsset(data);
                data.bgm = bgm.assetName;
                HLF.AssetMap.addAssetGroup(GameScreen.NAME, [
                    ...PostcardAssets,
                    ...Assets.locations[data.location]!,
                    bgm,
                ]);
                this.envelope.removeFromParent();
                data.envelope = this.envelope;
            }
            this.gotoNextState(screen, data);
        });

        this.globe.moveYTo(this.globe.y + this._size.y * 1.5, 0.6, Ease.sineIn);
        this.frontClouds.moveYTo(this.frontClouds.y + this.frontClouds.height, 0.2, Ease.sineIn);
        this.luna.moveYTo(this.luna.y + this.luna.characterSpine.height, 0.2, Ease.sineIn);

        this.buttonBack.alphaTo(0, 0.2);
        this.buttonNext.alphaTo(0, 0.2);
        this.buttonFreePlay.moveYTo(this._size.y + this.buttonFreePlay.height, 0.2, Ease.sineIn);
    }

    private startIdleInterval() {
        this.stopIdleInterval();
        if (!this._locationSelected) {
            this._idleVO.start();
        }
    }

    private stopIdleInterval() {
        this._idleVO.stop();
    }

    private playLocationVO() {
        const location = Locations[this._locationIndex];
        VoiceOverManager.instance.playVO(getLocationNameVO(location), voOptionsDirections);
    }
}
